import { Controller } from "stimulus"

// Ticket 1735: Duo PT op de tablet. Beide sporters staan op één pagina, elk in
// een eigen paneel naast elkaar. Deze controller wisselt tussen de panelen
// zonder verzoek naar de server: door horizontaal te swipen over het
// trainingsgedeelte, met de tabs "Bart | Linda" of met het wisselknopje.
//
// Een swipe telt pas als de vinger duidelijk horizontaal beweegt: eerst
// RICHTING_PX in een richting, met een hoek van hooguit ~30 graden ten
// opzichte van de horizontaal (HOEK), en bij loslaten minstens DREMPEL_PX.
// Een tik, een verticale scroll of een knijpbeweging is dus nooit een swipe.
// Na een swipe wordt de klik onder de vinger genegeerd, zodat swipen geen
// gewicht, herhaling of vinkje verandert.
//
// Per paar sporters onthoudt sessionStorage welk paneel open stond en waar elk
// paneel gescrold was, zodat ook een bewuste herlaadbeurt (een notitie
// opslaan, een oefening vervangen) op dezelfde plek terugkomt.
const RICHTING_PX = 12
const HOEK = 0.58
const DREMPEL_PX = 60
const KLIK_NEGEREN_MS = 400

export default class extends Controller {
  static targets = ["baan", "paneel", "tab", "teller", "naam", "knopNaam", "keuze", "wissel"]
  static values = { actief: Number, sleutel: String, titels: Array, teller: String }

  connect() {
    const opgeslagen = this.leesOpslag()
    if (Number.isInteger(opgeslagen.actief) && opgeslagen.actief >= 0 && opgeslagen.actief < this.paneelTargets.length) {
      this.actiefValue = opgeslagen.actief
    }

    this.zetHoogte = this.zetHoogte.bind(this)
    this.start = this.start.bind(this)
    this.beweeg = this.beweeg.bind(this)
    this.einde = this.einde.bind(this)
    this.bewaarScroll = this.bewaarScroll.bind(this)

    window.addEventListener("resize", this.zetHoogte)
    this.baanTarget.addEventListener("touchstart", this.start, { passive: true })
    this.baanTarget.addEventListener("touchmove", this.beweeg, { passive: false })
    this.baanTarget.addEventListener("touchend", this.einde)
    this.baanTarget.addEventListener("touchcancel", this.einde)
    this.paneelTargets.forEach((paneel) => {
      const top = (opgeslagen.scroll || {})[paneel.dataset.contentId]
      if (Number.isFinite(top)) paneel.scrollTop = top
      paneel.addEventListener("scroll", this.bewaarScroll, { passive: true })
    })

    this.zetHoogte()
    this.toon(this.actiefValue, false)
    this.element.dataset.duoKlaar = "1"
  }

  disconnect() {
    window.removeEventListener("resize", this.zetHoogte)
    this.baanTarget.removeEventListener("touchstart", this.start)
    this.baanTarget.removeEventListener("touchmove", this.beweeg)
    this.baanTarget.removeEventListener("touchend", this.einde)
    this.baanTarget.removeEventListener("touchcancel", this.einde)
    this.paneelTargets.forEach((paneel) => paneel.removeEventListener("scroll", this.bewaarScroll))
  }

  // Tabs, wisselknopje en "Ga naar training ..." (data-duo-pt-index-param)
  kies(event) {
    event.preventDefault()
    const index = Number(event.params.index)
    if (Number.isInteger(index)) this.toon(index)
  }

  // Pijltjestoetsen op de tabs
  toets(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()
    this.toon(this.actiefValue + (event.key === "ArrowRight" ? 1 : -1))
    const tab = this.tabTargets[this.actiefValue]
    if (tab) tab.focus()
  }

  toon(index, animeer = true) {
    const laatste = this.paneelTargets.length - 1
    const doel = Math.max(0, Math.min(laatste, index))
    this.actiefValue = doel
    const links = doel * this.baanTarget.clientWidth
    const rustig = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (animeer && !rustig && typeof this.baanTarget.scrollTo === "function") {
      this.baanTarget.scrollTo({ left: links, behavior: "smooth" })
    } else {
      this.baanTarget.scrollLeft = links
    }
    this.markeer()
    if (this.hasWisselTarget) this.wisselTarget.removeAttribute("open")
    this.schrijfOpslag()
  }

  // Zet alle aanwijzingen van de actieve sporter goed. Ook na het vervangen
  // van de kop door de server (afronden, heropenen).
  markeer() {
    const actief = this.actiefValue
    this.paneelTargets.forEach((paneel, i) => {
      const open = i === actief
      paneel.classList.toggle("ca-duo-actief", open)
      paneel.toggleAttribute("inert", !open)
      paneel.setAttribute("aria-hidden", open ? "false" : "true")
    })
    this.tabTargets.forEach((tab, i) => {
      tab.setAttribute("aria-selected", i === actief ? "true" : "false")
      tab.tabIndex = i === actief ? 0 : -1
    })
    this.keuzeTargets.forEach((keuze, i) => keuze.classList.toggle("ca-duo-keuze-actief", i === actief))
    const tekst = this.tellerValue.replace("{nr}", String(actief + 1))
    this.tellerTargets.forEach((teller) => { teller.textContent = tekst })
    const titel = this.titelsValue[actief]
    if (titel && this.hasNaamTarget) this.naamTarget.textContent = titel
    const tab = this.tabTargets[actief]
    const naam = tab && tab.querySelector(".ca-duo-tab-naam")
    if (naam) this.knopNaamTargets.forEach((knop) => { knop.textContent = naam.textContent })
  }

  tabTargetConnected() { this.markeerLater() }
  tellerTargetConnected() { this.markeerLater() }
  keuzeTargetConnected() { this.markeerLater() }

  markeerLater() {
    if (!this.element.dataset.duoKlaar) return
    if (this.markeerGepland) return
    this.markeerGepland = true
    requestAnimationFrame(() => {
      this.markeerGepland = false
      this.markeer()
    })
  }

  // De panelen vullen het scherm tot onderaan; de rustbalk ligt eroverheen en
  // de paneelinhoud houdt daar ruimte voor vrij.
  zetHoogte() {
    const boven = this.baanTarget.getBoundingClientRect().top + window.scrollY
    const hoogte = Math.max(320, window.innerHeight - boven)
    this.element.style.setProperty("--ca-duo-hoogte", `${hoogte}px`)
    this.baanTarget.scrollLeft = this.actiefValue * this.baanTarget.clientWidth
  }

  start(event) {
    this.gebaar = null
    if (event.touches.length !== 1) return
    // Ingezoomd: horizontaal bewegen is dan rondkijken, geen wissel.
    if (window.visualViewport && window.visualViewport.scale > 1.01) return
    const doel = event.target
    if (doel.closest && doel.closest("textarea, select, details[open] .ca-popover-paneel, [data-duo-geen-swipe]")) return
    const aanraking = event.touches[0]
    this.gebaar = { x: aanraking.clientX, y: aanraking.clientY, dx: 0, richting: null,
                    basis: this.actiefValue * this.baanTarget.clientWidth }
  }

  beweeg(event) {
    const gebaar = this.gebaar
    if (!gebaar) return
    if (event.touches.length !== 1) { this.annuleer(); return }
    const aanraking = event.touches[0]
    const dx = aanraking.clientX - gebaar.x
    const dy = aanraking.clientY - gebaar.y

    if (!gebaar.richting) {
      if (Math.abs(dx) < RICHTING_PX && Math.abs(dy) < RICHTING_PX) return
      gebaar.richting = Math.abs(dy) <= Math.abs(dx) * HOEK ? "horizontaal" : "verticaal"
      if (gebaar.richting === "verticaal") { this.gebaar = null; return }
      this.element.classList.add("ca-duo-slepen")
    }

    event.preventDefault()
    gebaar.dx = dx
    const max = (this.paneelTargets.length - 1) * this.baanTarget.clientWidth
    this.baanTarget.scrollLeft = Math.max(0, Math.min(max, gebaar.basis - dx))
  }

  einde() {
    const gebaar = this.gebaar
    this.gebaar = null
    if (!gebaar || gebaar.richting !== "horizontaal") return
    this.element.classList.remove("ca-duo-slepen")
    this.negeerVolgendeKlik()
    let doel = this.actiefValue
    if (Math.abs(gebaar.dx) >= DREMPEL_PX) doel += gebaar.dx < 0 ? 1 : -1
    this.toon(doel)
  }

  annuleer() {
    if (this.gebaar && this.gebaar.richting === "horizontaal") this.toon(this.actiefValue)
    this.gebaar = null
    this.element.classList.remove("ca-duo-slepen")
  }

  negeerVolgendeKlik() {
    const negeer = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }
    this.element.addEventListener("click", negeer, { capture: true, once: true })
    setTimeout(() => this.element.removeEventListener("click", negeer, { capture: true }), KLIK_NEGEREN_MS)
  }

  bewaarScroll() {
    clearTimeout(this.scrollTimer)
    this.scrollTimer = setTimeout(() => this.schrijfOpslag(), 150)
  }

  opslagSleutel() {
    return `ca-duo:${this.sleutelValue}`
  }

  leesOpslag() {
    try {
      return JSON.parse(window.sessionStorage.getItem(this.opslagSleutel()) || "{}") || {}
    } catch (e) {
      return {}
    }
  }

  schrijfOpslag() {
    const scroll = {}
    this.paneelTargets.forEach((paneel) => { scroll[paneel.dataset.contentId] = paneel.scrollTop })
    try {
      window.sessionStorage.setItem(this.opslagSleutel(), JSON.stringify({ actief: this.actiefValue, scroll }))
    } catch (e) {
      // Geen opslag (privévenster): wisselen werkt gewoon, alleen herladen onthoudt niets.
    }
  }
}
